import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@store/store';
import { isMacosPlatform } from '@utils/auth';
import { FeatureItem } from 'src/types';
import { demoFeaturedItems } from '../../datas/demoFeatured';
import { DESKTOP_BREAKPOINT } from '../../hooks/useIsDesktop';

/**
 * Progress of the native (macOS) bridge sign-in: the shell injects tokens via
 * `window.setAuthTokens` after page load, and `useNativeBridgeAuth` exchanges
 * them. Route guards wait on this instead of redirecting to the login page.
 */
export type NativeAuthStatus = 'idle' | 'pending' | 'success' | 'error';

type GenreCategory =
  | 'music'
  | 'serenity'
  | 'book'
  | 'indian-music'
  | 'creation';

export interface PlaylistItem {
  id: string;
  name: string;
  description: string;
  category: GenreCategory;
  privacy: string;
  trackCount: number;
  createdAt: number;
  updatedAt: number;
  thumbnailUrl?: string;
  isTrackExists?: boolean;
}

export interface PlaylistsCategoryState {
  items: PlaylistItem[];
  loading: boolean;
}

export interface commonStoreState {
  sidebarOpen: boolean;
  activePopup: string;
  carouselItems: FeatureItem[];
  isMinimized: boolean;
  playlistOpen: boolean;
  genresByCategory: Record<GenreCategory, string[]>;
  genresLoading: Record<GenreCategory, boolean>;
  genresError: Record<GenreCategory, string | null>;
  isDirectPlayerAccess: boolean;
  playlistsByCategory: Record<GenreCategory, PlaylistsCategoryState>;
  blobUrlEnabled: boolean;
  isUnloggedGenerationModalOpen: boolean;
  selectedContributorSlug: string | null;
  nativeAuthStatus: NativeAuthStatus;
}

const initialState: commonStoreState = {
  sidebarOpen: false,
  activePopup: '',
  carouselItems: demoFeaturedItems,
  isMinimized: false,
  playlistOpen:
    typeof window !== 'undefined'
      ? window.innerWidth >= DESKTOP_BREAKPOINT
      : true, // Open by default only on desktop
  genresByCategory: {
    music: ['All'],
    serenity: ['All'],
    book: ['All'],
    'indian-music': ['All'],
    creation: ['All'],
  },
  genresLoading: {
    music: false,
    serenity: false,
    book: false,
    'indian-music': false,
    creation: false,
  },
  genresError: {
    music: null,
    serenity: null,
    book: null,
    'indian-music': null,
    creation: null,
  },
  isDirectPlayerAccess: false,
  playlistsByCategory: {
    music: { items: [], loading: false },
    serenity: { items: [], loading: false },
    book: { items: [], loading: false },
    'indian-music': { items: [], loading: false },
    creation: { items: [], loading: false },
  },
  blobUrlEnabled: sessionStorage.getItem('blobUrlEnabled') === 'true',
  isUnloggedGenerationModalOpen: false,
  selectedContributorSlug: null,
  // Only the URL param seeds this: `napster_platform` in localStorage survives
  // the session and would leave a plain browser stuck on 'pending' forever.
  nativeAuthStatus: isMacosPlatform() ? 'pending' : 'idle',
};

const commonStore = createSlice({
  name: 'common',
  initialState,
  reducers: {
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    setActivePopup: (state, action: PayloadAction<string>) => {
      state.activePopup = action.payload;
    },
    setIsMinimized: (state, action: PayloadAction<boolean>) => {
      state.isMinimized = action.payload;
    },
    setPlaylistOpen: (state, action: PayloadAction<boolean>) => {
      state.playlistOpen = action.payload;
    },
    setIsDirectPlayerAccess: (state, action: PayloadAction<boolean>) => {
      state.isDirectPlayerAccess = action.payload;
    },
    setIsUnloggedGenerationModalOpen: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isUnloggedGenerationModalOpen = action.payload;
    },
    setGenresLoading: (
      state,
      action: PayloadAction<{ category: GenreCategory; loading: boolean }>,
    ) => {
      const { category, loading } = action.payload;
      state.genresLoading[category] = loading;
    },
    setGenres: (
      state,
      action: PayloadAction<{ category: GenreCategory; genres: string[] }>,
    ) => {
      const { category, genres } = action.payload;
      state.genresByCategory[category] = ['All', ...genres];
      state.genresLoading[category] = false;
      state.genresError[category] = null;
    },
    setGenresError: (
      state,
      action: PayloadAction<{ category: GenreCategory; error: string }>,
    ) => {
      const { category, error } = action.payload;
      state.genresError[category] = error;
      state.genresLoading[category] = false;
    },
    // Playlist reducers
    setPlaylistsLoading: (
      state,
      action: PayloadAction<{ category: GenreCategory; loading: boolean }>,
    ) => {
      const { category, loading } = action.payload;
      state.playlistsByCategory[category].loading = loading;
    },
    setPlaylistsByCategory: (
      state,
      action: PayloadAction<{
        category: GenreCategory;
        playlists: PlaylistItem[];
      }>,
    ) => {
      const { category, playlists } = action.payload;
      state.playlistsByCategory[category].items = playlists;
      state.playlistsByCategory[category].loading = false;
    },
    updatePlaylistInCategory: (
      state,
      action: PayloadAction<{
        category: GenreCategory;
        playlist: PlaylistItem;
      }>,
    ) => {
      const { category, playlist } = action.payload;
      const index = state.playlistsByCategory[category].items.findIndex(
        (p) => p.id === playlist.id,
      );
      if (index !== -1) {
        state.playlistsByCategory[category].items[index] = playlist;
      }
    },
    updatePlaylistTrackCount: (
      state,
      action: PayloadAction<{
        category: GenreCategory;
        playlistId: string;
        trackCount: number;
      }>,
    ) => {
      const { category, playlistId, trackCount } = action.payload;
      const playlist = state.playlistsByCategory[category].items.find(
        (p) => p.id === playlistId,
      );
      if (playlist) {
        playlist.trackCount = trackCount;
      }
    },
    removePlaylistFromCategory: (
      state,
      action: PayloadAction<{
        category: GenreCategory;
        playlistId: string;
      }>,
    ) => {
      const { category, playlistId } = action.payload;
      state.playlistsByCategory[category].items = state.playlistsByCategory[
        category
      ].items.filter((p) => p.id !== playlistId);
    },
    setBlobUrlEnabled: (state, action: PayloadAction<boolean>) => {
      state.blobUrlEnabled = action.payload;
      sessionStorage.setItem('blobUrlEnabled', action.payload.toString());
    },
    setSelectedContributorSlug: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.selectedContributorSlug = action.payload;
    },
    setNativeAuthStatus: (state, action: PayloadAction<NativeAuthStatus>) => {
      state.nativeAuthStatus = action.payload;
    },
  },
});

export const {
  setSidebarOpen,
  setActivePopup,
  setIsMinimized,
  setPlaylistOpen,
  setIsDirectPlayerAccess,
  setIsUnloggedGenerationModalOpen,
  setGenres,
  setGenresLoading,
  setGenresError,
  setPlaylistsLoading,
  setPlaylistsByCategory,
  updatePlaylistInCategory,
  updatePlaylistTrackCount,
  removePlaylistFromCategory,
  setBlobUrlEnabled,
  setSelectedContributorSlug,
  setNativeAuthStatus,
} = commonStore.actions;
export const getCommonReducer = (state: RootState) => state.commonReducer;
export default commonStore.reducer;
